import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save, Edit2, GripVertical, Check, Settings } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Separator } from './ui/separator';
import { toast } from 'sonner';

/**
 * FormulaManager - Interface de configuration des formules
 * Permet de créer/modifier les groupes de choix, produits assignés, règles min/max
 */
const FormulaManager = ({
    api,
    products = [],
    categories = [],
    isOpen,
    onClose,
    onFormulasChange
}) => {
    const [formulas, setFormulas] = useState([]);
    const [supplements, setSupplements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingFormula, setEditingFormula] = useState(null);
    const [showFormulaDialog, setShowFormulaDialog] = useState(false);
    
    // Form state for formula editing
    const [formulaForm, setFormulaForm] = useState({
        name: '',
        description: '',
        price: 0,
        meal_type: 'dejeuner',
        badge: '',
        is_available: true,
        steps: [],
        available_supplements: []
    });

    // Load formulas and supplements
    useEffect(() => {
        if (isOpen) {
            loadData();
        }
    }, [isOpen]);

    const loadData = async () => {
        try {
            setLoading(true);
            // api can be a function that returns axios instance or axios instance itself
            const apiInstance = typeof api === 'function' ? api() : api;
            const [formulasRes, supplementsRes] = await Promise.all([
                apiInstance.get('/pos/formulas?available_only=false'),
                apiInstance.get('/pos/supplements').catch(() => ({ data: [] }))
            ]);
            setFormulas(formulasRes.data || []);
            setSupplements(supplementsRes.data || []);
        } catch (error) {
            console.error('Error loading formulas:', error);
            toast.error('Erreur lors du chargement des formules');
        } finally {
            setLoading(false);
        }
    };

    // Create new formula
    const handleCreateFormula = () => {
        setEditingFormula(null);
        setFormulaForm({
            name: '',
            description: '',
            price: 0,
            meal_type: 'dejeuner',
            badge: '',
            is_available: true,
            steps: [
                {
                    name: 'Plat',
                    required: true,
                    min_choices: 1,
                    max_choices: 1,
                    choices: []
                }
            ],
            available_supplements: []
        });
        setShowFormulaDialog(true);
    };

    // Edit existing formula
    const handleEditFormula = (formula) => {
        setEditingFormula(formula);
        setFormulaForm({
            name: formula.name || '',
            description: formula.description || '',
            price: formula.price || 0,
            meal_type: formula.meal_type || 'dejeuner',
            badge: formula.badge || '',
            is_available: formula.is_available !== false,
            steps: formula.steps?.map(step => ({
                name: step.name,
                required: step.required !== false,
                min_choices: step.min_choices || 1,
                max_choices: step.max_choices || 1,
                choices: step.choices?.map(c => ({
                    product_id: c.product_id,
                    product_name: c.product_name,
                    is_default: c.is_default || false,
                    price_adjustment: c.price_adjustment || 0,
                    destination: c.destination || 'cuisine'
                })) || []
            })) || [],
            available_supplements: formula.available_supplements || []
        });
        setShowFormulaDialog(true);
    };

    // Add new step
    const addStep = () => {
        setFormulaForm(prev => ({
            ...prev,
            steps: [...prev.steps, {
                name: `Groupe ${prev.steps.length + 1}`,
                required: true,
                min_choices: 1,
                max_choices: 1,
                choices: []
            }]
        }));
    };

    // Remove step
    const removeStep = (stepIndex) => {
        setFormulaForm(prev => ({
            ...prev,
            steps: prev.steps.filter((_, idx) => idx !== stepIndex)
        }));
    };

    // Update step
    const updateStep = (stepIndex, field, value) => {
        setFormulaForm(prev => ({
            ...prev,
            steps: prev.steps.map((step, idx) => 
                idx === stepIndex ? { ...step, [field]: value } : step
            )
        }));
    };

    // Add choice to step
    const addChoiceToStep = (stepIndex, product) => {
        setFormulaForm(prev => ({
            ...prev,
            steps: prev.steps.map((step, idx) => {
                if (idx !== stepIndex) return step;
                // Check if already added
                if (step.choices.some(c => c.product_id === product.id)) {
                    toast.info('Produit déjà ajouté à ce groupe');
                    return step;
                }
                return {
                    ...step,
                    choices: [...step.choices, {
                        product_id: product.id,
                        product_name: product.name,
                        is_default: step.choices.length === 0,
                        price_adjustment: 0,
                        destination: product.destination || 'cuisine'
                    }]
                };
            })
        }));
    };

    // Remove choice from step
    const removeChoiceFromStep = (stepIndex, choiceIndex) => {
        setFormulaForm(prev => ({
            ...prev,
            steps: prev.steps.map((step, idx) => {
                if (idx !== stepIndex) return step;
                return {
                    ...step,
                    choices: step.choices.filter((_, ci) => ci !== choiceIndex)
                };
            })
        }));
    };

    // Update choice
    const updateChoice = (stepIndex, choiceIndex, field, value) => {
        setFormulaForm(prev => ({
            ...prev,
            steps: prev.steps.map((step, idx) => {
                if (idx !== stepIndex) return step;
                return {
                    ...step,
                    choices: step.choices.map((choice, ci) => 
                        ci === choiceIndex ? { ...choice, [field]: value } : choice
                    )
                };
            })
        }));
    };

    // Add supplement to formula
    const addSupplement = () => {
        setFormulaForm(prev => ({
            ...prev,
            available_supplements: [...prev.available_supplements, {
                id: `sup-${Date.now()}`,
                name: '',
                price: 0,
                destination: 'cuisine'
            }]
        }));
    };

    // Update supplement
    const updateSupplement = (supIndex, field, value) => {
        setFormulaForm(prev => ({
            ...prev,
            available_supplements: prev.available_supplements.map((sup, idx) =>
                idx === supIndex ? { ...sup, [field]: value } : sup
            )
        }));
    };

    // Remove supplement
    const removeSupplement = (supIndex) => {
        setFormulaForm(prev => ({
            ...prev,
            available_supplements: prev.available_supplements.filter((_, idx) => idx !== supIndex)
        }));
    };

    // Save formula
    const handleSaveFormula = async () => {
        if (!formulaForm.name) {
            toast.error('Le nom de la formule est requis');
            return;
        }
        if (formulaForm.price <= 0) {
            toast.error('Le prix doit être supérieur à 0');
            return;
        }
        if (formulaForm.steps.length === 0) {
            toast.error('La formule doit avoir au moins un groupe de choix');
            return;
        }
        
        // Validate steps
        for (const step of formulaForm.steps) {
            if (!step.name) {
                toast.error('Chaque groupe doit avoir un nom');
                return;
            }
            if (step.choices.length === 0) {
                toast.error(`Le groupe "${step.name}" doit avoir au moins un choix`);
                return;
            }
        }

        try {
            const apiInstance = typeof api === 'function' ? api() : api;
            if (editingFormula) {
                await apiInstance.put(`/pos/formulas/${editingFormula.id}`, formulaForm);
                toast.success('Formule mise à jour');
            } else {
                await apiInstance.post('/pos/formulas', formulaForm);
                toast.success('Formule créée');
            }
            setShowFormulaDialog(false);
            loadData();
            onFormulasChange?.();
        } catch (error) {
            console.error('Error saving formula:', error);
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    // Delete formula
    const handleDeleteFormula = async (formulaId) => {
        if (!window.confirm('Supprimer cette formule ?')) return;
        
        try {
            const apiInstance = typeof api === 'function' ? api() : api;
            await apiInstance.delete(`/pos/formulas/${formulaId}`);
            toast.success('Formule supprimée');
            loadData();
            onFormulasChange?.();
        } catch (error) {
            console.error('Error deleting formula:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    if (!isOpen) return null;

    // Filter products by destination for suggestions
    const getProductsByDestination = (destination) => {
        return products.filter(p => {
            if (destination === 'cuisine') {
                return !p.destination || p.destination === 'cuisine';
            }
            return p.destination === destination;
        });
    };

    return (
        <>
            {/* Main Manager Dialog */}
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            Gestion des Formules
                        </DialogTitle>
                        <DialogDescription>
                            Configurez les groupes de choix, produits et suppléments pour chaque formule
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 overflow-hidden">
                        {loading ? (
                            <div className="flex items-center justify-center h-40">
                                <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
                            </div>
                        ) : (
                            <ScrollArea className="h-[60vh]">
                                <div className="space-y-3 p-1">
                                    {formulas.map((formula) => (
                                        <div 
                                            key={formula.id}
                                            className={cn(
                                                "p-4 rounded-xl border-2 transition-all",
                                                formula.is_available
                                                    ? "bg-white dark:bg-zinc-900 border-gray-200 dark:border-zinc-700"
                                                    : "bg-gray-50 dark:bg-zinc-800/50 border-gray-200 dark:border-zinc-700 opacity-60"
                                            )}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-bold text-lg">{formula.name}</h3>
                                                        {formula.badge && (
                                                            <Badge variant="secondary" className="text-xs">
                                                                {formula.badge}
                                                            </Badge>
                                                        )}
                                                        {!formula.is_available && (
                                                            <Badge variant="outline" className="text-xs text-red-600 border-red-200">
                                                                Indisponible
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        {formula.description || 'Pas de description'}
                                                    </p>
                                                    <div className="flex items-center gap-3 mt-2">
                                                        <Badge className="bg-emerald-100 text-emerald-700">
                                                            {formula.price?.toLocaleString()} F
                                                        </Badge>
                                                        <span className="text-xs text-muted-foreground">
                                                            {formula.steps?.length || 0} groupes
                                                        </span>
                                                        <span className="text-xs text-muted-foreground">
                                                            {formula.meal_type === 'petit_dejeuner' ? 'Petit déjeuner' : 
                                                             formula.meal_type === 'dejeuner' ? 'Déjeuner' : 
                                                             formula.meal_type === 'diner' ? 'Dîner' : 'Tout'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleEditFormula(formula)}
                                                    >
                                                        <Edit2 className="w-4 h-4 mr-1" />
                                                        Modifier
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => handleDeleteFormula(formula.id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                            
                                            {/* Steps preview */}
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {formula.steps?.map((step, idx) => (
                                                    <div 
                                                        key={idx}
                                                        className="px-2 py-1 bg-gray-100 dark:bg-zinc-800 rounded text-xs"
                                                    >
                                                        <span className="font-medium">{step.name}</span>
                                                        <span className="text-muted-foreground ml-1">
                                                            ({step.choices?.length || 0} choix)
                                                        </span>
                                                        {step.required && (
                                                            <span className="ml-1 text-red-500">*</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {formulas.length === 0 && (
                                        <div className="text-center py-10 text-muted-foreground">
                                            Aucune formule configurée
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                        )}
                    </div>

                    <DialogFooter className="border-t pt-4">
                        <Button variant="outline" onClick={onClose}>
                            Fermer
                        </Button>
                        <Button onClick={handleCreateFormula}>
                            <Plus className="w-4 h-4 mr-2" />
                            Nouvelle Formule
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Formula Edit Dialog */}
            <Dialog open={showFormulaDialog} onOpenChange={setShowFormulaDialog}>
                <DialogContent className="max-w-3xl max-h-[95vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>
                            {editingFormula ? `Modifier: ${editingFormula.name}` : 'Nouvelle Formule'}
                        </DialogTitle>
                    </DialogHeader>

                    <ScrollArea className="flex-1 pr-4">
                        <div className="space-y-6 p-1">
                            {/* Basic Info */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2 sm:col-span-1">
                                    <Label>Nom de la formule *</Label>
                                    <Input
                                        value={formulaForm.name}
                                        onChange={(e) => setFormulaForm(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Ex: Formule Business"
                                    />
                                </div>
                                <div>
                                    <Label>Prix (F CFA) *</Label>
                                    <Input
                                        type="number"
                                        value={formulaForm.price}
                                        onChange={(e) => setFormulaForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                                        placeholder="3500"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Label>Description</Label>
                                    <Input
                                        value={formulaForm.description}
                                        onChange={(e) => setFormulaForm(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="Description courte de la formule"
                                    />
                                </div>
                                <div>
                                    <Label>Type de repas</Label>
                                    <Select
                                        value={formulaForm.meal_type}
                                        onValueChange={(value) => setFormulaForm(prev => ({ ...prev, meal_type: value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="petit_dejeuner">Petit déjeuner</SelectItem>
                                            <SelectItem value="dejeuner">Déjeuner</SelectItem>
                                            <SelectItem value="diner">Dîner</SelectItem>
                                            <SelectItem value="all">Tout</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Badge (optionnel)</Label>
                                    <Select
                                        value={formulaForm.badge || 'none'}
                                        onValueChange={(value) => setFormulaForm(prev => ({ ...prev, badge: value === 'none' ? '' : value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Aucun" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Aucun</SelectItem>
                                            <SelectItem value="Populaire">Populaire</SelectItem>
                                            <SelectItem value="Premium">Premium</SelectItem>
                                            <SelectItem value="Business">Business</SelectItem>
                                            <SelectItem value="Nouveau">Nouveau</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Switch
                                        checked={formulaForm.is_available}
                                        onCheckedChange={(checked) => setFormulaForm(prev => ({ ...prev, is_available: checked }))}
                                    />
                                    <Label>Disponible à la vente</Label>
                                </div>
                            </div>

                            <Separator />

                            {/* Steps */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-semibold">Groupes de choix</h3>
                                    <Button size="sm" variant="outline" onClick={addStep}>
                                        <Plus className="w-4 h-4 mr-1" />
                                        Ajouter groupe
                                    </Button>
                                </div>
                                
                                <div className="space-y-4">
                                    {formulaForm.steps.map((step, stepIdx) => (
                                        <div 
                                            key={stepIdx}
                                            className="p-4 border rounded-xl bg-gray-50 dark:bg-zinc-800/50"
                                        >
                                            <div className="flex items-start gap-3">
                                                <GripVertical className="w-5 h-5 text-muted-foreground mt-2 cursor-grab" />
                                                <div className="flex-1 space-y-3">
                                                    {/* Step header */}
                                                    <div className="grid grid-cols-4 gap-3">
                                                        <div className="col-span-2">
                                                            <Label className="text-xs">Nom du groupe</Label>
                                                            <Input
                                                                value={step.name}
                                                                onChange={(e) => updateStep(stepIdx, 'name', e.target.value)}
                                                                placeholder="Ex: Plat, Boisson, Dessert"
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs">Min choix</Label>
                                                            <Select
                                                                value={step.min_choices?.toString() || '1'}
                                                                onValueChange={(v) => updateStep(stepIdx, 'min_choices', parseInt(v))}
                                                            >
                                                                <SelectTrigger>
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="0">0</SelectItem>
                                                                    <SelectItem value="1">1</SelectItem>
                                                                    <SelectItem value="2">2</SelectItem>
                                                                    <SelectItem value="3">3</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div>
                                                            <Label className="text-xs">Max choix</Label>
                                                            <Select
                                                                value={step.max_choices?.toString() || '1'}
                                                                onValueChange={(v) => updateStep(stepIdx, 'max_choices', parseInt(v))}
                                                            >
                                                                <SelectTrigger>
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="1">1</SelectItem>
                                                                    <SelectItem value="2">2</SelectItem>
                                                                    <SelectItem value="3">3</SelectItem>
                                                                    <SelectItem value="5">5</SelectItem>
                                                                    <SelectItem value="10">10</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="flex items-center gap-4">
                                                        <div className="flex items-center gap-2">
                                                            <Switch
                                                                checked={step.required}
                                                                onCheckedChange={(c) => updateStep(stepIdx, 'required', c)}
                                                            />
                                                            <span className="text-sm">Obligatoire</span>
                                                        </div>
                                                    </div>

                                                    {/* Choices in step */}
                                                    <div>
                                                        <Label className="text-xs mb-2 block">Produits dans ce groupe</Label>
                                                        <div className="flex flex-wrap gap-2 mb-2">
                                                            {step.choices.map((choice, choiceIdx) => (
                                                                <div
                                                                    key={choiceIdx}
                                                                    className="flex items-center gap-1 px-2 py-1 bg-white dark:bg-zinc-900 border rounded-lg text-sm"
                                                                >
                                                                    <span>{choice.product_name}</span>
                                                                    {choice.price_adjustment > 0 && (
                                                                        <Badge variant="outline" className="text-[10px] text-orange-600 ml-1">
                                                                            +{choice.price_adjustment}F
                                                                        </Badge>
                                                                    )}
                                                                    <Input
                                                                        type="number"
                                                                        className="w-16 h-6 text-xs ml-1"
                                                                        placeholder="+F"
                                                                        value={choice.price_adjustment || ''}
                                                                        onChange={(e) => updateChoice(stepIdx, choiceIdx, 'price_adjustment', parseFloat(e.target.value) || 0)}
                                                                    />
                                                                    <button
                                                                        onClick={() => removeChoiceFromStep(stepIdx, choiceIdx)}
                                                                        className="ml-1 text-red-500 hover:text-red-700"
                                                                    >
                                                                        <X className="w-3 h-3" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        
                                                        {/* Product selector */}
                                                        <Select
                                                            onValueChange={(productId) => {
                                                                const product = products.find(p => p.id === productId);
                                                                if (product) addChoiceToStep(stepIdx, product);
                                                            }}
                                                        >
                                                            <SelectTrigger className="w-full h-8 text-sm">
                                                                <SelectValue placeholder="+ Ajouter un produit..." />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {products.slice(0, 50).map(product => (
                                                                    <SelectItem key={product.id} value={product.id}>
                                                                        {product.name} ({product.category})
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="text-red-500 hover:text-red-700"
                                                    onClick={() => removeStep(stepIdx)}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <Separator />

                            {/* Supplements */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-semibold">Suppléments (optionnel)</h3>
                                    <Button size="sm" variant="outline" onClick={addSupplement}>
                                        <Plus className="w-4 h-4 mr-1" />
                                        Ajouter
                                    </Button>
                                </div>
                                
                                <div className="space-y-2">
                                    {formulaForm.available_supplements.map((sup, supIdx) => (
                                        <div key={supIdx} className="flex items-center gap-3 p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                                            <Input
                                                value={sup.name}
                                                onChange={(e) => updateSupplement(supIdx, 'name', e.target.value)}
                                                placeholder="Nom (ex: Fromage)"
                                                className="flex-1"
                                            />
                                            <div className="flex items-center gap-1">
                                                <span className="text-sm">+</span>
                                                <Input
                                                    type="number"
                                                    value={sup.price}
                                                    onChange={(e) => updateSupplement(supIdx, 'price', parseFloat(e.target.value) || 0)}
                                                    className="w-20"
                                                    placeholder="Prix"
                                                />
                                                <span className="text-sm">F</span>
                                            </div>
                                            <Select
                                                value={sup.destination}
                                                onValueChange={(v) => updateSupplement(supIdx, 'destination', v)}
                                            >
                                                <SelectTrigger className="w-32">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="cuisine">Cuisine</SelectItem>
                                                    <SelectItem value="bar">Bar</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="text-red-500"
                                                onClick={() => removeSupplement(supIdx)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    ))}
                                    
                                    {formulaForm.available_supplements.length === 0 && (
                                        <p className="text-sm text-muted-foreground text-center py-2">
                                            Aucun supplément configuré
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </ScrollArea>

                    <DialogFooter className="border-t pt-4">
                        <Button variant="outline" onClick={() => setShowFormulaDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSaveFormula}>
                            <Save className="w-4 h-4 mr-2" />
                            {editingFormula ? 'Enregistrer' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default FormulaManager;
